/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { WorkAccountingExport } from '../models/work-accounting-export';
import { WorkAccountingPartner } from '../models/work-accounting-partner';
import { WorkAttachVoucherReminderDto } from '../models/work-attach-voucher-reminder-dto';
import { WorkBookVouchersDto } from '../models/work-book-vouchers-dto';
import { WorkDocument } from '../models/work-document';
import { WorkDocumentPdfInfoDto } from '../models/work-document-pdf-info-dto';
import { WorkJournalEntryPreviewDto } from '../models/work-journal-entry-preview-dto';
import { WorkMarkVoucherPostedDto } from '../models/work-mark-voucher-posted-dto';
import { WorkMergeDocumentsDto } from '../models/work-merge-documents-dto';
import { WorkOrder } from '../models/work-order';
import { WorkPaginatedDocumented } from '../models/work-paginated-documented';
import { WorkSaveVoucherDto } from '../models/work-save-voucher-dto';
import { WorkSaveVoucherPartnerDefaultDto } from '../models/work-save-voucher-partner-default-dto';
import { WorkSplitDocumentDto } from '../models/work-split-document-dto';
import { WorkUpdateVoucherDto } from '../models/work-update-voucher-dto';
import { WorkVoucher } from '../models/work-voucher';
import { WorkVoucherAftercalcLineDto } from '../models/work-voucher-aftercalc-line-dto';
import { WorkVoucherApprovalDto } from '../models/work-voucher-approval-dto';
import { WorkVoucherAutoFillPreviewDto } from '../models/work-voucher-auto-fill-preview-dto';
import { WorkVoucherAutoFillRunDto } from '../models/work-voucher-auto-fill-run-dto';
import { WorkVoucherAutoFillStatusDto } from '../models/work-voucher-auto-fill-status-dto';
import { WorkVoucherDuplicateDto } from '../models/work-voucher-duplicate-dto';
import { WorkVoucherExportResultDto } from '../models/work-voucher-export-result-dto';
import { WorkVoucherInboxItemDto } from '../models/work-voucher-inbox-item-dto';
import { WorkVoucherPaymentSyncResultDto } from '../models/work-voucher-payment-sync-result-dto';
import { WorkVoucherProjectBookingDto } from '../models/work-voucher-project-booking-dto';
import { WorkVoucherRecognitionDto } from '../models/work-voucher-recognition-dto';

@Injectable({ providedIn: 'root' })
export class WorkVouchersV2Service extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `vouchersV2ControllerSavePartnerDefault()` */
  static readonly VouchersV2ControllerSavePartnerDefaultPath = '/v2/vouchers/partner-defaults';

  /**
   * Required permissions: vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerSavePartnerDefault()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerSavePartnerDefault$Response(
    params: {
      body: WorkSaveVoucherPartnerDefaultDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkAccountingPartner>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerSavePartnerDefaultPath, 'put');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkAccountingPartner>;
      })
    );
  }

  /**
   * Required permissions: vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerSavePartnerDefault$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerSavePartnerDefault(
    params: {
      body: WorkSaveVoucherPartnerDefaultDto
    },
    context?: HttpContext
  ): Observable<WorkAccountingPartner> {
    return this.vouchersV2ControllerSavePartnerDefault$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkAccountingPartner>): WorkAccountingPartner => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerFindInbox()` */
  static readonly VouchersV2ControllerFindInboxPath = '/v2/vouchers/inbox/{voucherType}';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerFindInbox()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindInbox$Response(
    params: {
      voucherType: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkVoucherInboxItemDto>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerFindInboxPath, 'get');
    if (params) {
      rb.path('voucherType', params.voucherType, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkVoucherInboxItemDto>>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerFindInbox$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindInbox(
    params: {
      voucherType: string;
    },
    context?: HttpContext
  ): Observable<Array<WorkVoucherInboxItemDto>> {
    return this.vouchersV2ControllerFindInbox$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkVoucherInboxItemDto>>): Array<WorkVoucherInboxItemDto> => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerFindAllPaginated()` */
  static readonly VouchersV2ControllerFindAllPaginatedPath = '/v2/vouchers';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:approve
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerFindAllPaginated()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindAllPaginated$Response(
    params?: {

    /**
     * Visum, komma-getrennt (oder-verknüpft): pending, approved, rejected
     */
      approval?: string;

    /**
     * Page number to retrieve.If you provide invalid value the default page number will applied
     *         <p>
     *              <b>Example: </b> 1
     *           </p>
     *         <p>
     *              <b>Default Value: </b> 1
     *           </p>
     */
      page?: number;

    /**
     * Number of records per page.
     *       <p>
     *              <b>Example: </b> 20
     *           </p>
     *       <p>
     *              <b>Default Value: </b> 50
     *           </p>
     *       <p>
     *              <b>Max Value: </b> 500
     *           </p>
     *
     *       If provided value is greater than max value, max value will be applied.
     */
      limit?: number;

    /**
     * Filter by voucherType query param.
     *           <p>
     *              <b>Format: </b> filter.voucherType={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.voucherType=$not:$like:John Doe&filter.voucherType=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li>
     * <li>$in</li></ul>
     */
      'filter.voucherType'?: Array<string>;

    /**
     * Filter by status query param.
     *           <p>
     *              <b>Format: </b> filter.status={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.status=$not:$like:John Doe&filter.status=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li>
     * <li>$in</li></ul>
     */
      'filter.status'?: Array<string>;

    /**
     * Filter by source query param.
     *           <p>
     *              <b>Format: </b> filter.source={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.source=$not:$like:John Doe&filter.source=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.source'?: Array<string>;

    /**
     * Filter by approvalRequired query param.
     *           <p>
     *              <b>Format: </b> filter.approvalRequired={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.approvalRequired=$not:$like:John Doe&filter.approvalRequired=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.approvalRequired'?: Array<string>;

    /**
     * Filter by accountingPartnerId query param.
     *           <p>
     *              <b>Format: </b> filter.accountingPartnerId={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.accountingPartnerId=$not:$like:John Doe&filter.accountingPartnerId=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.accountingPartnerId'?: Array<string>;

    /**
     * Filter by projectId query param.
     *           <p>
     *              <b>Format: </b> filter.projectId={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.projectId=$not:$like:John Doe&filter.projectId=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.projectId'?: Array<string>;

    /**
     * Filter by voucherDate query param.
     *           <p>
     *              <b>Format: </b> filter.voucherDate={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.voucherDate=$not:$like:John Doe&filter.voucherDate=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$gte</li>
     * <li>$lte</li>
     * <li>$btw</li></ul>
     */
      'filter.voucherDate'?: Array<string>;

    /**
     * Parameter to sort by.
     *       <p>To sort by multiple fields, just provide query param multiple types. The order in url defines an order of sorting</p>
     *       <p>
     *              <b>Format: </b> fieldName:DIRECTION
     *           </p>
     *       <p>
     *              <b>Example: </b> sortBy=id:DESC&sortBy=createdAt:ASC
     *           </p>
     *       <p>
     *              <b>Default Value: </b> id:DESC
     *           </p>
     *       <h4>Available Fields</h4><ul><li>id</li>
     * <li>voucherDate</li>
     * <li>dueDate</li>
     * <li>amountGross</li>
     * <li>status</li>
     * <li>created</li>
     * <li>modified</li>
     * <li>externalReference</li></ul>
     */
      sortBy?: Array<'id:ASC' | 'id:DESC' | 'voucherDate:ASC' | 'voucherDate:DESC' | 'dueDate:ASC' | 'dueDate:DESC' | 'amountGross:ASC' | 'amountGross:DESC' | 'status:ASC' | 'status:DESC' | 'created:ASC' | 'created:DESC' | 'modified:ASC' | 'modified:DESC' | 'externalReference:ASC' | 'externalReference:DESC'>;

    /**
     * Search term to filter result values
     *         <p>
     *              <b>Example: </b> John
     *           </p>
     *         <p>
     *              <b>Default Value: </b> No default value
     *           </p>
     */
      search?: string;

    /**
     * List of fields to search by term to filter result values
     *         <p>
     *              <b>Example: </b> externalReference,number,merchantName,notes,accountingPartner.name
     *           </p>
     *         <p>
     *              <b>Default Value: </b> By default all fields mentioned below will be used to search by term
     *           </p>
     *         <h4>Available Fields</h4><ul><li>externalReference</li>
     * <li>number</li>
     * <li>merchantName</li>
     * <li>notes</li>
     * <li>accountingPartner.name</li>
     * <li>accountingPartner.number</li></ul>
     */
      searchBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkPaginatedDocumented & {
'data'?: Array<WorkVoucher>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'voucherType'?: (string | Array<string>);
'status'?: (string | Array<string>);
'source'?: (string | Array<string>);
'approvalRequired'?: (string | Array<string>);
'accountingPartnerId'?: (string | Array<string>);
'projectId'?: (string | Array<string>);
'voucherDate'?: (string | Array<string>);
};
};
}>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerFindAllPaginatedPath, 'get');
    if (params) {
      rb.query('approval', params.approval, {});
      rb.query('page', params.page, {});
      rb.query('limit', params.limit, {});
      rb.query('filter.voucherType', params['filter.voucherType'], {});
      rb.query('filter.status', params['filter.status'], {});
      rb.query('filter.source', params['filter.source'], {});
      rb.query('filter.approvalRequired', params['filter.approvalRequired'], {});
      rb.query('filter.accountingPartnerId', params['filter.accountingPartnerId'], {});
      rb.query('filter.projectId', params['filter.projectId'], {});
      rb.query('filter.voucherDate', params['filter.voucherDate'], {});
      rb.query('sortBy', params.sortBy, {});
      rb.query('search', params.search, {});
      rb.query('searchBy', params.searchBy, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkPaginatedDocumented & {
        'data'?: Array<WorkVoucher>;
        'meta'?: {
        'select'?: Array<string>;
        'filter'?: {
        'voucherType'?: (string | Array<string>);
        'status'?: (string | Array<string>);
        'source'?: (string | Array<string>);
        'approvalRequired'?: (string | Array<string>);
        'accountingPartnerId'?: (string | Array<string>);
        'projectId'?: (string | Array<string>);
        'voucherDate'?: (string | Array<string>);
        };
        };
        }>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:approve
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerFindAllPaginated$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindAllPaginated(
    params?: {

    /**
     * Visum, komma-getrennt (oder-verknüpft): pending, approved, rejected
     */
      approval?: string;

    /**
     * Page number to retrieve.If you provide invalid value the default page number will applied
     *         <p>
     *              <b>Example: </b> 1
     *           </p>
     *         <p>
     *              <b>Default Value: </b> 1
     *           </p>
     */
      page?: number;

    /**
     * Number of records per page.
     *       <p>
     *              <b>Example: </b> 20
     *           </p>
     *       <p>
     *              <b>Default Value: </b> 50
     *           </p>
     *       <p>
     *              <b>Max Value: </b> 500
     *           </p>
     *
     *       If provided value is greater than max value, max value will be applied.
     */
      limit?: number;

    /**
     * Filter by voucherType query param.
     *           <p>
     *              <b>Format: </b> filter.voucherType={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.voucherType=$not:$like:John Doe&filter.voucherType=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li>
     * <li>$in</li></ul>
     */
      'filter.voucherType'?: Array<string>;

    /**
     * Filter by status query param.
     *           <p>
     *              <b>Format: </b> filter.status={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.status=$not:$like:John Doe&filter.status=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li>
     * <li>$in</li></ul>
     */
      'filter.status'?: Array<string>;

    /**
     * Filter by source query param.
     *           <p>
     *              <b>Format: </b> filter.source={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.source=$not:$like:John Doe&filter.source=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.source'?: Array<string>;

    /**
     * Filter by approvalRequired query param.
     *           <p>
     *              <b>Format: </b> filter.approvalRequired={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.approvalRequired=$not:$like:John Doe&filter.approvalRequired=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.approvalRequired'?: Array<string>;

    /**
     * Filter by accountingPartnerId query param.
     *           <p>
     *              <b>Format: </b> filter.accountingPartnerId={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.accountingPartnerId=$not:$like:John Doe&filter.accountingPartnerId=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.accountingPartnerId'?: Array<string>;

    /**
     * Filter by projectId query param.
     *           <p>
     *              <b>Format: </b> filter.projectId={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.projectId=$not:$like:John Doe&filter.projectId=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.projectId'?: Array<string>;

    /**
     * Filter by voucherDate query param.
     *           <p>
     *              <b>Format: </b> filter.voucherDate={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.voucherDate=$not:$like:John Doe&filter.voucherDate=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$gte</li>
     * <li>$lte</li>
     * <li>$btw</li></ul>
     */
      'filter.voucherDate'?: Array<string>;

    /**
     * Parameter to sort by.
     *       <p>To sort by multiple fields, just provide query param multiple types. The order in url defines an order of sorting</p>
     *       <p>
     *              <b>Format: </b> fieldName:DIRECTION
     *           </p>
     *       <p>
     *              <b>Example: </b> sortBy=id:DESC&sortBy=createdAt:ASC
     *           </p>
     *       <p>
     *              <b>Default Value: </b> id:DESC
     *           </p>
     *       <h4>Available Fields</h4><ul><li>id</li>
     * <li>voucherDate</li>
     * <li>dueDate</li>
     * <li>amountGross</li>
     * <li>status</li>
     * <li>created</li>
     * <li>modified</li>
     * <li>externalReference</li></ul>
     */
      sortBy?: Array<'id:ASC' | 'id:DESC' | 'voucherDate:ASC' | 'voucherDate:DESC' | 'dueDate:ASC' | 'dueDate:DESC' | 'amountGross:ASC' | 'amountGross:DESC' | 'status:ASC' | 'status:DESC' | 'created:ASC' | 'created:DESC' | 'modified:ASC' | 'modified:DESC' | 'externalReference:ASC' | 'externalReference:DESC'>;

    /**
     * Search term to filter result values
     *         <p>
     *              <b>Example: </b> John
     *           </p>
     *         <p>
     *              <b>Default Value: </b> No default value
     *           </p>
     */
      search?: string;

    /**
     * List of fields to search by term to filter result values
     *         <p>
     *              <b>Example: </b> externalReference,number,merchantName,notes,accountingPartner.name
     *           </p>
     *         <p>
     *              <b>Default Value: </b> By default all fields mentioned below will be used to search by term
     *           </p>
     *         <h4>Available Fields</h4><ul><li>externalReference</li>
     * <li>number</li>
     * <li>merchantName</li>
     * <li>notes</li>
     * <li>accountingPartner.name</li>
     * <li>accountingPartner.number</li></ul>
     */
      searchBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<WorkPaginatedDocumented & {
'data'?: Array<WorkVoucher>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'voucherType'?: (string | Array<string>);
'status'?: (string | Array<string>);
'source'?: (string | Array<string>);
'approvalRequired'?: (string | Array<string>);
'accountingPartnerId'?: (string | Array<string>);
'projectId'?: (string | Array<string>);
'voucherDate'?: (string | Array<string>);
};
};
}> {
    return this.vouchersV2ControllerFindAllPaginated$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkPaginatedDocumented & {
'data'?: Array<WorkVoucher>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'voucherType'?: (string | Array<string>);
'status'?: (string | Array<string>);
'source'?: (string | Array<string>);
'approvalRequired'?: (string | Array<string>);
'accountingPartnerId'?: (string | Array<string>);
'projectId'?: (string | Array<string>);
'voucherDate'?: (string | Array<string>);
};
};
}>): WorkPaginatedDocumented & {
'data'?: Array<WorkVoucher>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'voucherType'?: (string | Array<string>);
'status'?: (string | Array<string>);
'source'?: (string | Array<string>);
'approvalRequired'?: (string | Array<string>);
'accountingPartnerId'?: (string | Array<string>);
'projectId'?: (string | Array<string>);
'voucherDate'?: (string | Array<string>);
};
};
} => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerCreate()` */
  static readonly VouchersV2ControllerCreatePath = '/v2/vouchers';

  /**
   * Required permissions: vouchers:create, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerCreate$Response(
    params: {
      body: WorkSaveVoucherDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerCreate(
    params: {
      body: WorkSaveVoucherDto
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerCreate$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerFindPendingApprovals()` */
  static readonly VouchersV2ControllerFindPendingApprovalsPath = '/v2/vouchers/approvals/pending';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:approve
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerFindPendingApprovals()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindPendingApprovals$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkVoucher>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerFindPendingApprovalsPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkVoucher>>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:approve
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerFindPendingApprovals$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindPendingApprovals(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<WorkVoucher>> {
    return this.vouchersV2ControllerFindPendingApprovals$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkVoucher>>): Array<WorkVoucher> => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerFindOneByDocumentId()` */
  static readonly VouchersV2ControllerFindOneByDocumentIdPath = '/v2/vouchers/by-document/{documentId}';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:approve
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerFindOneByDocumentId()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindOneByDocumentId$Response(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerFindOneByDocumentIdPath, 'get');
    if (params) {
      rb.path('documentId', params.documentId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:approve
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerFindOneByDocumentId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindOneByDocumentId(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerFindOneByDocumentId$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerFindDuplicates()` */
  static readonly VouchersV2ControllerFindDuplicatesPath = '/v2/vouchers/lookups/duplicates';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerFindDuplicates()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindDuplicates$Response(
    params: {
      accountingPartnerId: number;
      externalReference: string;
      excludeVoucherId?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkVoucherDuplicateDto>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerFindDuplicatesPath, 'get');
    if (params) {
      rb.query('accountingPartnerId', params.accountingPartnerId, {});
      rb.query('externalReference', params.externalReference, {});
      rb.query('excludeVoucherId', params.excludeVoucherId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkVoucherDuplicateDto>>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerFindDuplicates$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindDuplicates(
    params: {
      accountingPartnerId: number;
      externalReference: string;
      excludeVoucherId?: number;
    },
    context?: HttpContext
  ): Observable<Array<WorkVoucherDuplicateDto>> {
    return this.vouchersV2ControllerFindDuplicates$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkVoucherDuplicateDto>>): Array<WorkVoucherDuplicateDto> => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerFindProjectBooking()` */
  static readonly VouchersV2ControllerFindProjectBookingPath = '/v2/vouchers/lookups/project-booking';

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerFindProjectBooking()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindProjectBooking$Response(
    params: {
      projectId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucherProjectBookingDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerFindProjectBookingPath, 'get');
    if (params) {
      rb.query('projectId', params.projectId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucherProjectBookingDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerFindProjectBooking$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindProjectBooking(
    params: {
      projectId: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucherProjectBookingDto> {
    return this.vouchersV2ControllerFindProjectBooking$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucherProjectBookingDto>): WorkVoucherProjectBookingDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerFindAftercalcLines()` */
  static readonly VouchersV2ControllerFindAftercalcLinesPath = '/v2/vouchers/lookups/aftercalc-lines';

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerFindAftercalcLines()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindAftercalcLines$Response(
    params?: {
      orderId?: number;

    /**
     * Projekt-IDs, komma-getrennt
     */
      projectIds?: string;
      voucherId?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkVoucherAftercalcLineDto>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerFindAftercalcLinesPath, 'get');
    if (params) {
      rb.query('orderId', params.orderId, {});
      rb.query('projectIds', params.projectIds, {});
      rb.query('voucherId', params.voucherId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkVoucherAftercalcLineDto>>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerFindAftercalcLines$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindAftercalcLines(
    params?: {
      orderId?: number;

    /**
     * Projekt-IDs, komma-getrennt
     */
      projectIds?: string;
      voucherId?: number;
    },
    context?: HttpContext
  ): Observable<Array<WorkVoucherAftercalcLineDto>> {
    return this.vouchersV2ControllerFindAftercalcLines$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkVoucherAftercalcLineDto>>): Array<WorkVoucherAftercalcLineDto> => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerFindOrder()` */
  static readonly VouchersV2ControllerFindOrderPath = '/v2/vouchers/lookups/orders';

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerFindOrder()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindOrder$Response(
    params: {
      number: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkOrder>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerFindOrderPath, 'get');
    if (params) {
      rb.query('number', params.number, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkOrder>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerFindOrder$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindOrder(
    params: {
      number: string;
    },
    context?: HttpContext
  ): Observable<WorkOrder> {
    return this.vouchersV2ControllerFindOrder$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkOrder>): WorkOrder => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerFindOneById()` */
  static readonly VouchersV2ControllerFindOneByIdPath = '/v2/vouchers/{id}';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:approve
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerFindOneById()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindOneById$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerFindOneByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:approve
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerFindOneById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindOneById(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerFindOneById$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerDelete()` */
  static readonly VouchersV2ControllerDeletePath = '/v2/vouchers/{id}';

  /**
   * Required permissions: vouchers:delete, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerDelete$Response(
    params: {
      id: number;
      withDocument?: boolean;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<boolean>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerDeletePath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('withDocument', params.withDocument, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: String((r as HttpResponse<any>).body) === 'true' }) as StrictHttpResponse<boolean>;
      })
    );
  }

  /**
   * Required permissions: vouchers:delete, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerDelete(
    params: {
      id: number;
      withDocument?: boolean;
    },
    context?: HttpContext
  ): Observable<boolean> {
    return this.vouchersV2ControllerDelete$Response(params, context).pipe(
      map((r: StrictHttpResponse<boolean>): boolean => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerUpdate()` */
  static readonly VouchersV2ControllerUpdatePath = '/v2/vouchers/{id}';

  /**
   * Required permissions: vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerUpdate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerUpdate$Response(
    params: {
      id: number;
      body: WorkUpdateVoucherDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerUpdatePath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * Required permissions: vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerUpdate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerUpdate(
    params: {
      id: number;
      body: WorkUpdateVoucherDto
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerUpdate$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerFindExports()` */
  static readonly VouchersV2ControllerFindExportsPath = '/v2/vouchers/{id}/exports';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerFindExports()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindExports$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkAccountingExport>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerFindExportsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkAccountingExport>>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerFindExports$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerFindExports(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<Array<WorkAccountingExport>> {
    return this.vouchersV2ControllerFindExports$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkAccountingExport>>): Array<WorkAccountingExport> => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerPreviewJournalEntry()` */
  static readonly VouchersV2ControllerPreviewJournalEntryPath = '/v2/vouchers/journal-entry-preview';

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerPreviewJournalEntry()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerPreviewJournalEntry$Response(
    params: {
      body: WorkSaveVoucherDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkJournalEntryPreviewDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerPreviewJournalEntryPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkJournalEntryPreviewDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerPreviewJournalEntry$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerPreviewJournalEntry(
    params: {
      body: WorkSaveVoucherDto
    },
    context?: HttpContext
  ): Observable<WorkJournalEntryPreviewDto> {
    return this.vouchersV2ControllerPreviewJournalEntry$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkJournalEntryPreviewDto>): WorkJournalEntryPreviewDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerRecognizeWithAi()` */
  static readonly VouchersV2ControllerRecognizeWithAiPath = '/v2/vouchers/recognize-ai/{documentId}';

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerRecognizeWithAi()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerRecognizeWithAi$Response(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucherRecognitionDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerRecognizeWithAiPath, 'post');
    if (params) {
      rb.path('documentId', params.documentId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucherRecognitionDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerRecognizeWithAi$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerRecognizeWithAi(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucherRecognitionDto> {
    return this.vouchersV2ControllerRecognizeWithAi$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucherRecognitionDto>): WorkVoucherRecognitionDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerAutoFillStatus()` */
  static readonly VouchersV2ControllerAutoFillStatusPath = '/v2/vouchers/auto-fill/status';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerAutoFillStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerAutoFillStatus$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucherAutoFillStatusDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerAutoFillStatusPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucherAutoFillStatusDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerAutoFillStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerAutoFillStatus(
    params?: {
    },
    context?: HttpContext
  ): Observable<WorkVoucherAutoFillStatusDto> {
    return this.vouchersV2ControllerAutoFillStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucherAutoFillStatusDto>): WorkVoucherAutoFillStatusDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerRunAutoFill()` */
  static readonly VouchersV2ControllerRunAutoFillPath = '/v2/vouchers/auto-fill/run';

  /**
   * Required permissions: vouchers:create, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerRunAutoFill()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerRunAutoFill$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucherAutoFillRunDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerRunAutoFillPath, 'post');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucherAutoFillRunDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerRunAutoFill$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerRunAutoFill(
    params?: {
    },
    context?: HttpContext
  ): Observable<WorkVoucherAutoFillRunDto> {
    return this.vouchersV2ControllerRunAutoFill$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucherAutoFillRunDto>): WorkVoucherAutoFillRunDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerCreateAutoFillDraft()` */
  static readonly VouchersV2ControllerCreateAutoFillDraftPath = '/v2/vouchers/auto-fill/{documentId}';

  /**
   * Required permissions: vouchers:create, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerCreateAutoFillDraft()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerCreateAutoFillDraft$Response(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerCreateAutoFillDraftPath, 'post');
    if (params) {
      rb.path('documentId', params.documentId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerCreateAutoFillDraft$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerCreateAutoFillDraft(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerCreateAutoFillDraft$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerPreviewAutoFillDraft()` */
  static readonly VouchersV2ControllerPreviewAutoFillDraftPath = '/v2/vouchers/auto-fill/{documentId}/preview';

  /**
   * Required permissions: vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerPreviewAutoFillDraft()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerPreviewAutoFillDraft$Response(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucherAutoFillPreviewDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerPreviewAutoFillDraftPath, 'post');
    if (params) {
      rb.path('documentId', params.documentId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucherAutoFillPreviewDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerPreviewAutoFillDraft$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerPreviewAutoFillDraft(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucherAutoFillPreviewDto> {
    return this.vouchersV2ControllerPreviewAutoFillDraft$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucherAutoFillPreviewDto>): WorkVoucherAutoFillPreviewDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerIsAiRecognitionAvailable()` */
  static readonly VouchersV2ControllerIsAiRecognitionAvailablePath = '/v2/vouchers/recognize-ai/available';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerIsAiRecognitionAvailable()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerIsAiRecognitionAvailable$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<boolean>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerIsAiRecognitionAvailablePath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: String((r as HttpResponse<any>).body) === 'true' }) as StrictHttpResponse<boolean>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerIsAiRecognitionAvailable$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerIsAiRecognitionAvailable(
    params?: {
    },
    context?: HttpContext
  ): Observable<boolean> {
    return this.vouchersV2ControllerIsAiRecognitionAvailable$Response(params, context).pipe(
      map((r: StrictHttpResponse<boolean>): boolean => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerRecognize()` */
  static readonly VouchersV2ControllerRecognizePath = '/v2/vouchers/recognize/{documentId}';

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerRecognize()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerRecognize$Response(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucherRecognitionDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerRecognizePath, 'post');
    if (params) {
      rb.path('documentId', params.documentId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucherRecognitionDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerRecognize$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerRecognize(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucherRecognitionDto> {
    return this.vouchersV2ControllerRecognize$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucherRecognitionDto>): WorkVoucherRecognitionDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerBookMany()` */
  static readonly VouchersV2ControllerBookManyPath = '/v2/vouchers/book';

  /**
   * Required permissions: vouchers:book, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerBookMany()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerBookMany$Response(
    params: {
      body: WorkBookVouchersDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkVoucherExportResultDto>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerBookManyPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkVoucherExportResultDto>>;
      })
    );
  }

  /**
   * Required permissions: vouchers:book, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerBookMany$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerBookMany(
    params: {
      body: WorkBookVouchersDto
    },
    context?: HttpContext
  ): Observable<Array<WorkVoucherExportResultDto>> {
    return this.vouchersV2ControllerBookMany$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkVoucherExportResultDto>>): Array<WorkVoucherExportResultDto> => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerSyncPayments()` */
  static readonly VouchersV2ControllerSyncPaymentsPath = '/v2/vouchers/sync-payments';

  /**
   * Required permissions: vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerSyncPayments()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerSyncPayments$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucherPaymentSyncResultDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerSyncPaymentsPath, 'post');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucherPaymentSyncResultDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerSyncPayments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerSyncPayments(
    params?: {
    },
    context?: HttpContext
  ): Observable<WorkVoucherPaymentSyncResultDto> {
    return this.vouchersV2ControllerSyncPayments$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucherPaymentSyncResultDto>): WorkVoucherPaymentSyncResultDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerApprove()` */
  static readonly VouchersV2ControllerApprovePath = '/v2/vouchers/{id}/approve';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerApprove()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerApprove$Response(
    params: {
      id: number;
      body: WorkVoucherApprovalDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerApprovePath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerApprove$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerApprove(
    params: {
      id: number;
      body: WorkVoucherApprovalDto
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerApprove$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerReject()` */
  static readonly VouchersV2ControllerRejectPath = '/v2/vouchers/{id}/reject';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerReject()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerReject$Response(
    params: {
      id: number;
      body: WorkVoucherApprovalDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerRejectPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerReject$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerReject(
    params: {
      id: number;
      body: WorkVoucherApprovalDto
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerReject$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerMarkPosted()` */
  static readonly VouchersV2ControllerMarkPostedPath = '/v2/vouchers/{id}/mark-posted';

  /**
   * Required permissions: vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerMarkPosted()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerMarkPosted$Response(
    params: {
      id: number;
      body: WorkMarkVoucherPostedDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerMarkPostedPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * Required permissions: vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerMarkPosted$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerMarkPosted(
    params: {
      id: number;
      body: WorkMarkVoucherPostedDto
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerMarkPosted$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerUnmarkPosted()` */
  static readonly VouchersV2ControllerUnmarkPostedPath = '/v2/vouchers/{id}/unmark-posted';

  /**
   * Required permissions: vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerUnmarkPosted()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerUnmarkPosted$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerUnmarkPostedPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * Required permissions: vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerUnmarkPosted$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerUnmarkPosted(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerUnmarkPosted$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerAttachReminder()` */
  static readonly VouchersV2ControllerAttachReminderPath = '/v2/vouchers/{id}/reminders';

  /**
   * Required permissions: vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerAttachReminder()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerAttachReminder$Response(
    params: {
      id: number;
      body: WorkAttachVoucherReminderDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerAttachReminderPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * Required permissions: vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerAttachReminder$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerAttachReminder(
    params: {
      id: number;
      body: WorkAttachVoucherReminderDto
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerAttachReminder$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerResetApproval()` */
  static readonly VouchersV2ControllerResetApprovalPath = '/v2/vouchers/{id}/reset-approval';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:approve
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerResetApproval()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerResetApproval$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucher>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerResetApprovalPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucher>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:approve
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerResetApproval$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerResetApproval(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucher> {
    return this.vouchersV2ControllerResetApproval$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucher>): WorkVoucher => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerBook()` */
  static readonly VouchersV2ControllerBookPath = '/v2/vouchers/{id}/book';

  /**
   * Required permissions: vouchers:book, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerBook()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerBook$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucherExportResultDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerBookPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucherExportResultDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:book, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerBook$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerBook(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucherExportResultDto> {
    return this.vouchersV2ControllerBook$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucherExportResultDto>): WorkVoucherExportResultDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerExportPreview()` */
  static readonly VouchersV2ControllerExportPreviewPath = '/v2/vouchers/{id}/export-preview';

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:book
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerExportPreview()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerExportPreview$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucherExportResultDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerExportPreviewPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucherExportResultDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:read, vouchers:admin, vouchers:create, vouchers:update, vouchers:admin, vouchers:book
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerExportPreview$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerExportPreview(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucherExportResultDto> {
    return this.vouchersV2ControllerExportPreview$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucherExportResultDto>): WorkVoucherExportResultDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerAttachDocument()` */
  static readonly VouchersV2ControllerAttachDocumentPath = '/v2/vouchers/{id}/attach-document';

  /**
   * Required permissions: vouchers:book, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerAttachDocument()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerAttachDocument$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkVoucherExportResultDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerAttachDocumentPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkVoucherExportResultDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:book, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerAttachDocument$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerAttachDocument(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkVoucherExportResultDto> {
    return this.vouchersV2ControllerAttachDocument$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkVoucherExportResultDto>): WorkVoucherExportResultDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerDiscardInboxDocument()` */
  static readonly VouchersV2ControllerDiscardInboxDocumentPath = '/v2/vouchers/inbox/documents/{documentId}';

  /**
   * Required permissions: vouchers:delete, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerDiscardInboxDocument()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerDiscardInboxDocument$Response(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<boolean>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerDiscardInboxDocumentPath, 'delete');
    if (params) {
      rb.path('documentId', params.documentId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: String((r as HttpResponse<any>).body) === 'true' }) as StrictHttpResponse<boolean>;
      })
    );
  }

  /**
   * Required permissions: vouchers:delete, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerDiscardInboxDocument$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerDiscardInboxDocument(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<boolean> {
    return this.vouchersV2ControllerDiscardInboxDocument$Response(params, context).pipe(
      map((r: StrictHttpResponse<boolean>): boolean => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerInboxPdfInfo()` */
  static readonly VouchersV2ControllerInboxPdfInfoPath = '/v2/vouchers/inbox/documents/{documentId}/pdf-info';

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerInboxPdfInfo()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerInboxPdfInfo$Response(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkDocumentPdfInfoDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerInboxPdfInfoPath, 'get');
    if (params) {
      rb.path('documentId', params.documentId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkDocumentPdfInfoDto>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerInboxPdfInfo$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  vouchersV2ControllerInboxPdfInfo(
    params: {
      documentId: number;
    },
    context?: HttpContext
  ): Observable<WorkDocumentPdfInfoDto> {
    return this.vouchersV2ControllerInboxPdfInfo$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkDocumentPdfInfoDto>): WorkDocumentPdfInfoDto => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerSplitInboxDocument()` */
  static readonly VouchersV2ControllerSplitInboxDocumentPath = '/v2/vouchers/inbox/documents/{documentId}/split';

  /**
   * Required permissions: vouchers:create, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerSplitInboxDocument()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerSplitInboxDocument$Response(
    params: {
      documentId: number;
      body: WorkSplitDocumentDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkDocument>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerSplitInboxDocumentPath, 'post');
    if (params) {
      rb.path('documentId', params.documentId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkDocument>>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerSplitInboxDocument$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerSplitInboxDocument(
    params: {
      documentId: number;
      body: WorkSplitDocumentDto
    },
    context?: HttpContext
  ): Observable<Array<WorkDocument>> {
    return this.vouchersV2ControllerSplitInboxDocument$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkDocument>>): Array<WorkDocument> => r.body)
    );
  }

  /** Path part for operation `vouchersV2ControllerMergeInboxDocuments()` */
  static readonly VouchersV2ControllerMergeInboxDocumentsPath = '/v2/vouchers/inbox/documents/merge';

  /**
   * Required permissions: vouchers:create, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `vouchersV2ControllerMergeInboxDocuments()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerMergeInboxDocuments$Response(
    params: {
      body: WorkMergeDocumentsDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkDocument>> {
    const rb = new RequestBuilder(this.rootUrl, WorkVouchersV2Service.VouchersV2ControllerMergeInboxDocumentsPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkDocument>;
      })
    );
  }

  /**
   * Required permissions: vouchers:create, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `vouchersV2ControllerMergeInboxDocuments$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  vouchersV2ControllerMergeInboxDocuments(
    params: {
      body: WorkMergeDocumentsDto
    },
    context?: HttpContext
  ): Observable<WorkDocument> {
    return this.vouchersV2ControllerMergeInboxDocuments$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkDocument>): WorkDocument => r.body)
    );
  }

}
